import { Button } from "flowbite-react/components/Button";
import { Card } from "flowbite-react/components/Card";
import { Sidebar, SidebarItem, SidebarItemGroup, SidebarItems } from "flowbite-react/components/Sidebar";
import { ArrowRight, BookOpen, Home, LogIn, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import StitchFlowLayout from "@/components/stitch/StitchFlowLayout";

export default function ConsultingProgramsPage() {
  return (
    <StitchFlowLayout
      pageClassName="stitch-flow-page--portal"
      contentClassName="stitch-portal-content"
    >
      <section className="stitch-portal-home md:hidden" aria-labelledby="portal-title">
        <div className="stitch-portal-hero">
          <div className="stitch-portal-intro">
            <p className="stitch-portal-eyebrow">Kişisel rehberlik portalınız</p>
            <h2 id="portal-title">Pleiades Meditasyon Takip Portalı</h2>
            <p className="stitch-portal-description">
              Size özel hazırlanan günlük çalışmalarınızı takip edin, deneyimlerinizi kaydedin
              ve rehberlik sürecinizin bütününü tek bir yerde görün.
            </p>

            <div className="stitch-portal-action-group">
              <Link to="/giris" className="stitch-portal-login">
                <span>Portalınıza giriş yap</span>
                <ArrowRight size={20} strokeWidth={2} aria-hidden="true" />
              </Link>
              <p>Davet kodunuz veya hesabınızla güvenli biçimde devam edin.</p>
            </div>
          </div>
        </div>
      </section>

      <section
        className="hidden min-h-[calc(100dvh-72px)] bg-pleiades-ivory md:flex"
        aria-labelledby="desktop-portal-title"
        data-testid="flowbite-desktop-portal"
      >
        <Sidebar
          aria-label="Pleiades portal menüsü"
          className="w-64 shrink-0 border-r border-pleiades-border [&>div]:rounded-none [&>div]:bg-pleiades-sidebar [&>div]:px-4 [&>div]:py-6"
        >
          <div className="mb-8 border-b border-pleiades-border px-2 pb-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-pleiades-slate">
              Pleiades / Portal
            </p>
            <p className="mt-3 font-editorial text-2xl leading-tight text-pleiades-ink">
              Kişisel rehberlik alanınız
            </p>
          </div>
          <SidebarItems>
            <SidebarItemGroup>
              <SidebarItem href="#portal-baslangic" icon={Home} className="rounded-sm text-pleiades-ink hover:bg-white">
                Portal başlangıcı
              </SidebarItem>
              <SidebarItem href="#portal-olanaklar" icon={BookOpen} className="rounded-sm text-pleiades-ink hover:bg-white">
                Neler yapabilirsiniz?
              </SidebarItem>
              <SidebarItem href="#portal-guvenlik" icon={ShieldCheck} className="rounded-sm text-pleiades-ink hover:bg-white">
                Güvenli giriş
              </SidebarItem>
            </SidebarItemGroup>
          </SidebarItems>
        </Sidebar>

        <div className="min-w-0 flex-1">
          <header className="flex min-h-[72px] items-center justify-between border-b border-pleiades-border bg-white px-8 lg:px-10">
            <div>
              <p className="text-sm text-pleiades-slate">Kişisel rehberlik portalı</p>
              <strong className="text-base font-semibold text-pleiades-ink">Pleiades Rehberlik</strong>
            </div>
            <Button
              as={Link}
              to="/giris"
              color="purple"
              className="rounded-sm bg-pleiades-blue px-2 focus:ring-pleiades-gold hover:bg-pleiades-blue-hover"
            >
              <LogIn className="mr-2 h-4 w-4" aria-hidden="true" />
              Giriş yap
            </Button>
          </header>

          <main className="mx-auto max-w-7xl px-8 py-8 lg:px-10 lg:py-10">
            <div id="portal-baslangic" className="grid gap-6 xl:grid-cols-[minmax(0,1.55fr)_minmax(300px,0.75fr)]">
              <Card className="rounded-sm border-pleiades-border bg-white shadow-none [&>div]:gap-0 [&>div]:p-8 lg:[&>div]:p-10">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-pleiades-blue">
                  Kişisel rehberlik portalınız
                </p>
                <h1 id="desktop-portal-title" className="mt-5 max-w-3xl font-editorial text-4xl leading-[1.08] text-pleiades-ink lg:text-5xl">
                  Pleiades Meditasyon Takip Portalı
                </h1>
                <p className="mt-6 max-w-2xl text-lg leading-8 text-pleiades-slate">
                  Size özel hazırlanan günlük çalışmalarınızı takip edin, deneyimlerinizi kaydedin
                  ve rehberlik sürecinizin bütününü tek bir yerde görün.
                </p>
                <div className="mt-9 flex flex-wrap items-center gap-4">
                  <Button
                    as={Link}
                    to="/giris"
                    color="purple"
                    size="lg"
                    className="rounded-sm bg-pleiades-blue focus:ring-pleiades-gold hover:bg-pleiades-blue-hover"
                  >
                    Portalınıza giriş yap
                    <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                  </Button>
                  <span className="text-sm text-pleiades-slate">Davet kodunuz veya hesabınızla devam edin.</span>
                </div>
              </Card>

              <Card id="portal-guvenlik" className="rounded-sm border-pleiades-border bg-pleiades-sidebar shadow-none [&>div]:p-7">
                <ShieldCheck className="h-7 w-7 text-pleiades-blue" aria-hidden="true" />
                <p className="font-mono text-xs text-pleiades-slate">P / 01</p>
                <h2 className="font-editorial text-2xl text-pleiades-ink">Güvenli ve kişisel erişim</h2>
                <p className="leading-7 text-pleiades-slate">
                  Portal yalnız hesabınız veya size özel oluşturulan davet bağlantısıyla açılır.
                </p>
              </Card>
            </div>

            <div id="portal-olanaklar" className="mt-6 grid gap-4 lg:grid-cols-3">
              {[
                ["01", "Günlük çalışmalar", "Size atanan çalışmaları ve uygulama günlerini tek ekranda takip edin."],
                ["02", "Deneyim kayıtları", "Uygulama sonrasında fark ettiklerinizi güvenli biçimde kaydedin."],
                ["03", "Süreç görünümü", "Tamamlanan ve sıradaki adımları bütünlük içinde görün."],
              ].map(([number, title, description]) => (
                <Card key={number} className="rounded-sm border-pleiades-border bg-white shadow-none [&>div]:gap-3 [&>div]:p-6">
                  <span className="font-mono text-xs text-pleiades-blue">{number}</span>
                  <h2 className="text-xl font-semibold text-pleiades-ink">{title}</h2>
                  <p className="leading-6 text-pleiades-slate">{description}</p>
                </Card>
              ))}
            </div>

          </main>
        </div>
      </section>
    </StitchFlowLayout>
  );
}
